import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { nutztEineFunktion, nutztFunktion } from "@/office/excel/formeln";
import { formulaCells, isError, readWorkbook, allCells } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionBedingt: Lesson = {
  id: "bedingt",
  title: "Bedingt rechnen: WENN und Verwandte",
  summary: "Funktionen, die eine Frage stellen, bevor sie rechnen – der Einstieg in echte Auswertungen.",
  durationMinutes: 13,
  learningGoals: [
    "Mit WENN zwei Fälle unterscheiden",
    "Mit SUMMEWENN und ZÄHLENWENN gezielt auswerten",
    "Fehlerwerte mit WENNFEHLER abfangen, ohne sie zu verstecken",
  ],
  steps: [
    {
      id: "wenn",
      title: "WENN: Prüfung, Dann-Wert, Sonst-Wert",
      explanation: [
        "WENN hat drei Argumente: eine Prüfung, einen Wert für den Fall, dass sie " +
          "zutrifft, und einen für den Fall, dass sie nicht zutrifft. " +
          "=WENN(B2>1000;\"Großauftrag\";\"Normal\") liest sich fast wie ein deutscher " +
          "Satz. Die Prüfung ist dabei alles, was wahr oder falsch sein kann – ein " +
          "Vergleich mit >, <, =, >=, <= oder <>.",
        "Texte in einer Formel gehören in gerade Anführungszeichen. Fehlen sie, hält " +
          "Excel das Wort für einen benannten Bereich und meldet #NAME?. Zahlen dagegen " +
          "stehen ohne Anführungszeichen – wer eine Zahl in Anführungszeichen setzt, " +
          "bekommt Text zurück, mit dem sich nicht weiterrechnen lässt.",
        "WENN lässt sich verschachteln, indem im Sonst-Zweig wieder ein WENN steht. Bis " +
          "zu zwei Ebenen ist das noch lesbar, darüber wird es unübersichtlich. Für " +
          "mehr Fälle sind ein SVERWEIS auf eine kleine Zuordnungstabelle oder die " +
          "neuere Funktion WENNS die bessere Wahl – und eine Zuordnungstabelle hat den " +
          "Vorteil, dass sich die Regeln ändern lassen, ohne eine Formel anzufassen.",
      ],
      keyPoints: [
        "=WENN(Prüfung;Dann;Sonst) – drei Argumente, durch Semikolon getrennt.",
        "Text in der Formel gehört in gerade Anführungszeichen, Zahlen nicht.",
        "Ab drei Fällen ist eine Zuordnungstabelle übersichtlicher als geschachtelte WENN.",
      ],
      task: {
        instruction:
          "Ergänze deine Liste um eine Spalte, die mit WENN zwei Fälle unterscheidet – " +
          "zum Beispiel „Großauftrag“ ab einem bestimmten Betrag, sonst „Normal“.",
        hint: "=WENN(D2>1000;\"Großauftrag\";\"Normal\") – und die Formel danach nach unten ausfüllen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort eine WENN-Funktion mit drei Argumenten steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const wenn = formeln.filter((zelle) => nutztFunktion(zelle.formula, "wenn"));

          if (wenn.length === 0) {
            return fail(
              formeln.length === 0
                ? "Es steht noch keine Formel in der Arbeitsmappe."
                : `Von ${formeln.length} Formeln benutzt keine die Funktion WENN.`,
              "Schreibe in eine freie Spalte etwa =WENN(D2>1000;\"Großauftrag\";\"Normal\") und passe " +
                "den Bezug an deine Zahlenspalte an.",
            );
          }

          const erste = wenn[0];
          if (wenn.length < 3) {
            return pass(
              `In ${erste.address} steht „${erste.formula}“ und ergibt „${erste.text}“. ` +
                "Fülle die Formel noch über die ganze Spalte aus – dann arbeitet sie für jede Zeile.",
            );
          }

          return pass(
            `${wenn.length} Zellen unterscheiden mit WENN zwei Fälle, zum Beispiel ` +
              `${erste.address} mit „${erste.formula}“.`,
          );
        },
      },
    },
    {
      id: "summewenn",
      title: "SUMMEWENN und ZÄHLENWENN",
      explanation: [
        "SUMME addiert alles. SUMMEWENN addiert nur das, was zu einer Bedingung passt: " +
          "=SUMMEWENN(B2:B100;\"Nord\";D2:D100) summiert die Beträge aus Spalte D, aber " +
          "nur für die Zeilen, in denen in Spalte B „Nord“ steht. Die Argumente sind " +
          "Suchbereich, Kriterium, Summenbereich – in dieser Reihenfolge, und der " +
          "Summenbereich zuletzt, was regelmäßig verwechselt wird.",
        "ZÄHLENWENN funktioniert genauso, zählt aber statt zu addieren und braucht " +
          "deshalb nur zwei Argumente. Mit ihr lässt sich in einer Zeile beantworten, " +
          "wie oft ein Wert vorkommt – und damit auch, ob eine Liste Dubletten enthält.",
        "Als Kriterium geht mehr als ein fester Wert: \">1000\" für alle größeren, " +
          "\"<>Nord\" für alle außer Nord, \"Nord*\" für alles, was mit Nord beginnt. " +
          "Wichtig ist, dass das Kriterium in Anführungszeichen steht, sobald ein " +
          "Vergleichsoperator darin vorkommt.",
        "Der bessere Weg ist meist, das Kriterium nicht zu tippen, sondern auf eine " +
          "Zelle zu zeigen: =SUMMEWENN(B2:B100;F1;D2:D100). Dann steht die Region in F1 " +
          "und lässt sich ändern, ohne die Formel zu öffnen – dieselbe Regel wie bei " +
          "den festen Zahlen im vorigen Modul.",
      ],
      keyPoints: [
        "=SUMMEWENN(Suchbereich;Kriterium;Summenbereich) – der Summenbereich steht hinten.",
        "ZÄHLENWENN braucht nur Suchbereich und Kriterium.",
        "Kriterien mit Operator gehören in Anführungszeichen: \">1000\".",
      ],
      task: {
        instruction:
          "Werte deine Liste mit SUMMEWENN oder ZÄHLENWENN gezielt aus – zum Beispiel " +
          "die Summe einer einzelnen Region oder die Anzahl der Zeilen über einem " +
          "bestimmten Betrag.",
        hint: "Schreibe das Kriterium in eine eigene Zelle und zeige in der Formel darauf, statt es zu tippen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort SUMMEWENN oder ZÄHLENWENN steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const bedingt = formeln.filter((zelle) =>
            nutztEineFunktion(zelle.formula, ["summewenn", "summewenns", "zaehlenwenn", "zaehlenwenns"]),
          );

          if (bedingt.length === 0) {
            return fail(
              `Von ${formeln.length} Formeln benutzt keine SUMMEWENN oder ZÄHLENWENN.`,
              "Zum Beispiel: =SUMMEWENN(B2:B20;\"Nord\";D2:D20). Der Summenbereich steht am Ende.",
            );
          }

          const erste = bedingt[0];
          return pass(
            `In ${erste.address} wertet „${erste.formula}“ gezielt aus und liefert „${erste.text}“.`,
          );
        },
      },
    },
    {
      id: "wennfehler",
      title: "WENNFEHLER: aufräumen, nicht vertuschen",
      explanation: [
        "Manche Fehlerwerte sind unvermeidlich und trotzdem störend. Eine " +
          "Prozentrechnung, deren Nenner noch leer ist, zeigt #DIV/0!, bis die Daten " +
          "da sind. Ein SVERWEIS auf einen noch nicht eingetragenen Artikel zeigt #NV. " +
          "In einem Bericht sieht das nach einem kaputten Dokument aus.",
        "WENNFEHLER fängt das ab: =WENNFEHLER(B2/C2;\"\") zeigt das Ergebnis, wenn es " +
          "eines gibt, und sonst nichts. Statt der leeren Zeichenkette lässt sich auch " +
          "ein Strich oder ein Hinweis einsetzen – oft besser, weil eine leere Zelle " +
          "nicht von einer noch nicht ausgefüllten zu unterscheiden ist.",
        "Und jetzt die Warnung, die zu WENNFEHLER dazugehört: Es unterdrückt jeden " +
          "Fehler, auch den, den du sehen solltest. Ein #BEZUG! nach einer gelöschten " +
          "Zeile verschwindet damit genauso spurlos wie ein erwartetes #NV. Wer eine " +
          "ganze Tabelle vorsorglich in WENNFEHLER packt, macht sie blind gegen ihre " +
          "eigenen Fehler.",
        "Die Faustregel: WENNFEHLER nur dort einsetzen, wo du den Fehler benennen " +
          "kannst, der auftreten wird – und nachdem die Formel einmal fehlerfrei " +
          "gelaufen ist. Nicht vorher, und nicht flächendeckend.",
      ],
      keyPoints: [
        "=WENNFEHLER(Formel;Ersatzwert) fängt jeden Fehler ab.",
        "Ein Strich als Ersatzwert ist meist besser lesbar als eine leere Zelle.",
        "Erst prüfen, dann abfangen – sonst versteckt WENNFEHLER echte Fehler.",
      ],
      task: {
        instruction:
          "Fange in einer deiner Formeln einen erwartbaren Fehler mit WENNFEHLER ab – " +
          "etwa eine Division, deren Nenner leer sein kann.",
        hint: "=WENNFEHLER(D2/C2;\"–\") zeigt einen Strich statt #DIV/0!, solange C2 leer ist.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob deine Formel in WENNFEHLER eingefasst ist.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const abgefangen = formeln.filter((zelle) => nutztFunktion(zelle.formula, "wennfehler"));

          if (abgefangen.length === 0) {
            return fail(
              `Von ${formeln.length} Formeln fängt keine ihre Fehler mit WENNFEHLER ab.`,
              "Nimm eine Formel, die einen Fehler zeigen könnte, und schreibe sie um: " +
                "aus =D2/C2 wird =WENNFEHLER(D2/C2;\"–\").",
            );
          }

          // Ein flächendeckendes WENNFEHLER wäre das Gegenteil der Lehre dieses
          // Schritts - deshalb wird darauf hingewiesen, ohne die Aufgabe zu
          // verweigern.
          const anteil = abgefangen.length / formeln.length;
          const erste = abgefangen[0];
          if (anteil > 0.8 && formeln.length >= 5) {
            return pass(
              `In ${erste.address} fängt „${erste.formula}“ den Fehler ab. Ein Hinweis dazu: ` +
                `${abgefangen.length} von ${formeln.length} Formeln sind in WENNFEHLER eingefasst. ` +
                "Das ist reichlich – eine Tabelle, die jeden Fehler unterdrückt, meldet auch " +
                "keinen echten mehr.",
            );
          }

          const offeneFehler = allCells(mappe).filter(isError);
          return pass(
            `In ${erste.address} fängt „${erste.formula}“ einen erwartbaren Fehler ab.` +
              (offeneFehler.length > 0
                ? ` An anderer Stelle stehen noch ${offeneFehler.length} Fehlerwerte – die gehören ` +
                  "behoben, nicht abgefangen."
                : ""),
          );
        },
      },
    },
  ],
};
